import { APIEntity, dataManager, registerEntity } from '../APIEntity';
import { IEntity, EntityMerge } from '../IEntity';
import { ActionInfo } from '../models/ActionInfo';
import { ICloudOrigin, ICloudOriginLocal } from '../models/CloudOrigin';
import { IMessageEnvelope } from '../models/MessageEnvelope';
import { IMessageSender } from '../models/MessageSender';
import { Callable } from '../types';
import { ConnectionManager, DataOp } from '../websocket';

export enum DeliveryMode {
  EMAIL = 'email',
  REPO = 'repo',
}

export enum AttachmentType {
  TYPE_ID = 'type_id',
  FILE = 'file',
  REPO = 'repo',
  URL = 'url',
  PROMPT = 'prompt',
}

/** Lifecycle of a FlowMessage's body bundle on the hub. Mirrors
 *  flow_sdk.builtin.flow_message.BodyStatus exactly.
 *  - NA        : no body needed (text-only, or inline-only attachments).
 *  - UPLOADING : sender is staging the body; receivers must wait.
 *  - READY     : body is available at fs/download/<BODY_FILENAME>.
 *  - FAILED    : an upload attempt failed; the sender's outbox retries it (receivers wait).
 *  NA is terminal; UPLOADING / FAILED → READY. */
export enum BodyStatus {
  NA = 'na',
  UPLOADING = 'uploading',
  READY = 'ready',
  FAILED = 'failed',
}

/** Discriminator for special FlowMessage kinds. Mirrors
 *  flow_sdk.builtin.flow_message.FlowMessageKind exactly.
 *  - USER       : a normal message (the default for everything the user or
 *                 hub produces).
 *  - INVITATION : a local-only placeholder FlowMessage representing a pending
 *                 hub Invitation as the first row of a conversation; its
 *                 ``context_entities`` carry the backing Invitation TypeId so
 *                 the UI can read invitation_id off it for the Accept action.
 *  - SESSION_EVENT : a live-session lifecycle line ("Dana approved the live
 *                 session") rendered as a slim system line, not a bubble.
 *                 Doubles as the session-snapshot carrier via its
 *                 remote_worker_session TYPE_ID attachment. */
export enum FlowMessageKind {
  USER = 'user',
  INVITATION = 'invitation',
  SESSION_EVENT = 'session_event',
  /** Secured transfer: the quiet, automatic line of a key exchange in the share's thread (a slim line). */
  SEALED_NOTE = 'sealed_note',
}

/** Single source of truth for the body filename on the hub blob store.
 *  Must match flow_sdk.builtin.flow_message.BODY_FILENAME exactly — the
 *  parity unit test asserts this literal. */
export const BODY_FILENAME = 'body.flowmsg';

/** VFS subpath prefix for PROMPT-with-file attachments. Mirrors
 *  flow_sdk.builtin.flow_message.PROMPT_FILE_VFS_PREFIX. */
const PROMPT_FILE_VFS_PREFIX = 'prompt/';

/** Thrown by downloadBody() when called on an FM whose body_status != READY. */
export class BodyNotReadyError extends Error {
  constructor(status: BodyStatus | undefined) {
    super(`download_body refused: body_status=${status} (must be READY)`);
    this.name = 'BodyNotReadyError';
  }
}

/** Read an Attachment's ``data`` field as a canonical string.
 *
 * The hub sometimes deserializes TYPE_ID attachments with ``data`` as a
 * parsed ``{type, id}`` TypeId object rather than the ``"<type>-<uuid>"``
 * string every UI consumer expects (chip rendering, ``.startsWith('prompt/')``,
 * ``.endsWith('.jsonl')``, ``.split('/')`` for filenames). Use this helper
 * at every read site so the shape variance is collapsed in one place.
 * Returns ``''`` when ``data`` is missing/unrecognized so consumers can use
 * length checks instead of typeof guards. */
export function attachmentDataString(a: Pick<Attachment, 'data'>): string {
  const d = a.data as unknown;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const obj = d as { type?: string; id?: string };
    if (typeof obj.type === 'string' && typeof obj.id === 'string') {
      return `${obj.type}-${obj.id}`;
    }
  }
  return '';
}

/** `{id, value}` from the first `<kind>-<id>` TYPE_ID attachment — a JSON object riding its
 *  `prompt_preview` (a value, not an entity), the mirror of `carried_value` in `flow_message.py`.
 *  A carrier whose preview is missing or not a JSON object reads as none. */
export function carriedValue<T extends object>(
  attachments: readonly Attachment[] | null | undefined,
  kind: string,
): { id: string; value: T } | null {
  const prefix = `${kind}-`;
  for (const a of attachments ?? []) {
    if (a?.attachment_type !== AttachmentType.TYPE_ID) continue;
    const data = attachmentDataString(a);
    if (!data.startsWith(prefix)) continue;
    try {
      const value: unknown = JSON.parse(a.prompt_preview ?? '');
      return value && typeof value === 'object' ? { id: data.slice(prefix.length), value: value as T } : null;
    } catch {
      return null;
    }
  }
  return null;
}

export interface Attachment {
  attachment_type: AttachmentType;
  /** TypeId string ("type-id"), relative file path, repo path, URL, or — for PROMPT — inline text or "prompt/<filename>" VFS subpath. */
  data: string;
  /** Absolute filesystem path — populated server-side for FILE / PROMPT-file attachments, null for others. */
  local_path?: string | null;
  /** Prompt-entity TYPE_ID attachments: inline copy of the prompt text so receivers
   *  can preview before the body bundle is downloaded. On a session carrier it holds
   *  the session marker JSON instead. NOTE: this field must also exist on the HUB's
   *  Attachment model — the hub silently drops unknown fields on the round-trip.
   *  Consent is a property of the session, never stamped per attachment. */
  prompt_preview?: string | null;
}

export type AttachmentReference = Pick<Attachment, 'attachment_type' | 'data'>;

/** Read the backend's missing-asset result; pending downloads are not warnings. */
export function isAttachmentMissing(
  fm: Pick<IFlowMessage, 'body_downloaded' | 'body_missing_attachments'>,
  attachment: AttachmentReference,
): boolean {
  const data = attachmentDataString(attachment);
  return (
    !!fm.body_downloaded &&
    (fm.body_missing_attachments ?? []).some(
      (missing) => missing.attachment_type === attachment.attachment_type && attachmentDataString(missing) === data,
    )
  );
}

/** Delivery receipt. Mirrors the hub-side schema. Monotonic transitions only:
 *  created → sent → delivered → received.
 *  - pending_send: composed while signed out; held locally until a login sends it (🕐 Pending)
 *  - created:   local only, hub has not accepted it (🕐 Pending)
 *  - sent:      accepted/stored on the hub (✓)
 *  - delivered: recipient's client pulled it (✓✓)
 *  - received:  recipient read it (✓✓ blue) */
export type DeliveryStatus = 'pending_send' | 'created' | 'sent' | 'delivered' | 'received';

/** Named event types emitted by ``Conversation`` and ``FlowMessage``. Use
 *  these instead of bare strings so call sites are typo-proof:
 *    conversation.on(ConversationEvents.MESSAGE, msg => ...)  // new inbound message
 *    conversation.on(ConversationEvents.ACK, msg => ...)      // a receipt changed
 *    message.on(ConversationEvents.ACK, () => ...)            // this message's receipt
 *    await message.waitForAck(timeoutMs)                      // resolve once received
 */
export enum ConversationEvents {
  /** A new inbound FlowMessage arrived on the conversation. */
  MESSAGE = 'message',
  /** A FlowMessage's delivery_status changed (delivered / received). */
  ACK = 'ack',
}

export interface IFlowMessage extends IEntity {
  text?: string;
  instruction?: string | null;
  attachment?: Attachment[];
  sender_id?: string | null;
  sender_name?: string | null;
  /** The typed author, local only — read it through `senderOf`, which covers hub rows. */
  sender?: IMessageSender | null;
  receiver_address?: string | null;
  receiver_address_type?: string | null;
  /** User-given filename of the uploaded .flowmsg zip stored via fs/upload, e.g. "my-share.flowmsg". Null when no file was uploaded. */
  attachment_filename?: string | null;
  /** ID of the parent Conversation; null on legacy messages predating the field. */
  conversation_id?: string | null;
  is_read?: boolean;
  is_archived?: boolean;
  /** Receipt state — orthogonal to the local-only `is_read` flag. Set only
   *  by the hub via mark_delivered / mark_received actions; flows back to
   *  the sender as a data_op_msg(update) frame, subject to the parent
   *  reporting user's message-status sharing preference. */
  delivery_status?: DeliveryStatus;
  delivered_at?: string | null;
  received_at?: string | null;
  /** EVENT time — when the human sent this on its original channel. Written
   *  only by the backend stream inbox projection (from SourceItem.occurred_at);
   *  None for authored and hub-synced messages, whose event time is their
   *  own created/updated clock. Read through `eventTime`, never directly. */
  sent_at?: string | null;
  // NOTE: ``context`` (string[]) was renamed and consolidated into the
  // unified ``context_entities`` on IEntity. Read via
  // ``msg.contextEntities`` / ``msg.firstContextOfType('task')``.
  /** Local-only draft message: not appended to conversation.jsonl, not pushed to hub. Flips to false on send-draft. */
  is_draft?: boolean;
  /** Special-message discriminator. USER (default) is a normal message;
   *  INVITATION marks a local-only placeholder representing a pending hub
   *  Invitation as the first row of a conversation. The invitation TypeId
   *  lives in ``context_entities``. */
  kind?: FlowMessageKind;
  /** This machine wrote it to send (the outbox's "mine"). Local only. */
  outbound?: boolean;
  /** Why the hub does not have it yet — set on a failed attempt, cleared when it lands. Local only. */
  delivery_failure?: import('./help-request').HubFailure | null;
  /** Body-bundle lifecycle on the hub. Defaults to NA when the message has
   *  no body. Stamped UPLOADING at hub add_message time when the incoming
   *  attachments require a packed body; sender flips to READY after upload.
   *  Receivers gate downloads on READY. */
  body_status?: BodyStatus;
  /** Why the body is FAILED, in user-readable words (set by the hub, e.g. when
   *  its stored copy was lost). Null otherwise. */
  body_error?: string | null;
  /** Live-session grouping key. Stamped at send time by the guest (who mints
   *  the session id) and on PromptCompletion replies by the host; receivers
   *  re-derive it from the `remote_worker_session-<id>` TYPE_ID attachment
   *  when the hub stripped the header field. */
  remote_worker_session_id?: string | null;
  /** Transient, server-derived. True when the bundle is unpacked, or all its
   *  assets are already local. A downloaded bundle can have missing assets. */
  body_downloaded?: boolean;
  /** Transient, server-derived references whose bytes are unavailable locally.
   *  With body_downloaded=true these are missing assets, not pending downloads. */
  body_missing_attachments?: AttachmentReference[];
  /** Transient, server-derived. True when the bundle's extracted tree persists
   *  under the message's staging dir — staged attachments are reviewable (and
   *  MessageAttachment rows exist) even though nothing is installed/indexed. */
  body_unpacked?: boolean;
  /** Forward provenance: id of the source FlowMessage this one was forwarded
   *  from (set only on forwarded clones — see forwardMessage). */
  cloned_from_id?: string | null;
  /** Original sender of the source message (for the "forwarded" chip). */
  cloned_from_sender_id?: string | null;
  /** Where the real record lives when this message CACHES a cloud one (a Gmail
   *  message, a Slack post). Null/absent means the message is ours — which is
   *  exactly the badge rule: no origin, no channel mark. */
  origin?: ICloudOrigin | null;
  origin_local?: ICloudOriginLocal | null;
  /** Sender, recipients, subject and event time of the cached record; projection-owned. */
  envelope?: IMessageEnvelope | null;
  /** The MessageThread this belongs to. Null = ungrouped, i.e. flat rendering
   *  (every message that predates threading). */
  thread_id?: string | null;
  /** Id of the message this replies to — the quote. On Flowpad's own chat it is the hub id, so it
   *  names the same message on every member's machine. */
  reply_to_id?: string | null;
  /** Flowpad's own chat: the message whose thread this one is in (the native thread's wire fact;
   *  the backend resolves it to `thread_id`). Null on channel messages. */
  thread_root_id?: string | null;
  /** Set on a REFERENCE row: the SourceItem whose body this message renders.
   *  The stored row's `text` is always empty; reads arrive hydrated. */
  source_item_id?: string | null;
  /** Who reacted with what on a channel message — projection-owned. */
  reactions?: IMessageReaction[];
  /** What the channel message did (approved, merged) — projection-owned; null when it only says something. */
  mark?: IMessageMark | null;
}

/** What a channel message did, drawn as a chip before its text — mirrors `MessageMarkSpec`
 *  (flow_sdk/sources/values/items.py). `key` is what an action's `done_mark` matches. */
export interface IMessageMark {
  key: string;
  label: string;
  tone?: 'positive' | 'negative' | 'neutral';
}

/** One person's emoji on a channel message — mirrors `MessageReaction`
 *  (flow_sdk/schema/data_spec/message_reaction_spec.py). `ours` marks the ones this machine put there. */
export interface IMessageReaction {
  emoji: string;
  by: string;
  by_name?: string | null;
  ours?: boolean;
  at?: string | null;
}

// `implements IFlowMessage` only checks the class; it contributes no members, so every
// field declared solely on IFlowMessage read as "does not exist". deepAssign populates
// them from the wire — this merge makes them part of the class type.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FlowMessage extends EntityMerge<IFlowMessage> {}

@registerEntity
export class FlowMessage extends APIEntity<FlowMessage> implements IFlowMessage {
  text?: string;
  instruction?: string | null;
  attachment?: Attachment[];
  sender_id?: string | null;
  sender_name?: string | null;
  sender?: IMessageSender | null;
  receiver_address?: string | null;
  receiver_address_type?: string | null;
  attachment_filename?: string | null;
  conversation_id?: string | null;
  is_read?: boolean;
  is_archived?: boolean;
  delivery_status?: DeliveryStatus;
  delivered_at?: string | null;
  received_at?: string | null;
  sent_at?: string | null;
  is_draft?: boolean;
  kind?: FlowMessageKind;
  body_status?: BodyStatus;
  body_error?: string | null;
  outbound?: boolean;
  delivery_failure?: import('./help-request').HubFailure | null;
  body_downloaded?: boolean;
  body_missing_attachments?: AttachmentReference[];
  body_unpacked?: boolean;
  cloned_from_id?: string | null;
  cloned_from_sender_id?: string | null;
  remote_worker_session_id?: string | null;
  origin?: ICloudOrigin | null;
  origin_local?: ICloudOriginLocal | null;
  /** Sender, recipients, subject and event time of the cached record; projection-owned. */
  envelope?: IMessageEnvelope | null;
  thread_id?: string | null;
  reply_to_id?: string | null;
  thread_root_id?: string | null;
  source_item_id?: string | null;
  reactions?: IMessageReaction[];
  mark?: IMessageMark | null;
  static type: string = 'flow_message';

  /** An unnamed message reads as a short quote of its text (chips, tabs); a given name wins. */
  getDisplayName(): string | null {
    if (this.name?.trim()) return null;
    const text = this.text?.trim().replace(/\s+/g, ' ');
    if (!text) return null;
    return `“${text.length > 32 ? `${text.slice(0, 32)}…` : text}”`;
  }

  constructor(entity: Partial<IFlowMessage> = {}) {
    super(entity);
    this.text = entity.text;
    this.instruction = entity.instruction;
    this.attachment = entity.attachment;
    this.sender_id = entity.sender_id;
    this.sender_name = entity.sender_name;
    this.sender = entity.sender ?? null;
    this.receiver_address = entity.receiver_address;
    this.receiver_address_type = entity.receiver_address_type;
    this.attachment_filename = entity.attachment_filename;
    this.conversation_id = entity.conversation_id;
    this.is_read = entity.is_read ?? false;
    this.is_archived = entity.is_archived ?? false;
    this.delivery_status = entity.delivery_status ?? 'created';
    this.delivered_at = entity.delivered_at ?? null;
    this.received_at = entity.received_at ?? null;
    this.sent_at = entity.sent_at ?? null;
    this.is_draft = entity.is_draft ?? false;
    this.kind = entity.kind ?? FlowMessageKind.USER;
    this.body_status = entity.body_status ?? BodyStatus.NA;
    this.body_error = entity.body_error ?? null;
    this.outbound = entity.outbound ?? false;
    this.delivery_failure = entity.delivery_failure ?? null;
    this.body_downloaded = entity.body_downloaded ?? false;
    this.body_missing_attachments = entity.body_missing_attachments ?? [];
    this.body_unpacked = entity.body_unpacked ?? false;
    this.cloned_from_id = entity.cloned_from_id ?? null;
    this.cloned_from_sender_id = entity.cloned_from_sender_id ?? null;
    this.remote_worker_session_id = entity.remote_worker_session_id ?? null;
    this.origin = entity.origin ?? null;
    this.origin_local = entity.origin_local ?? null;
    this.envelope = entity.envelope ?? null;
    this.thread_id = entity.thread_id ?? null;
    this.reply_to_id = entity.reply_to_id ?? null;
    this.thread_root_id = entity.thread_root_id ?? null;
    this.source_item_id = entity.source_item_id ?? null;
    this.reactions = entity.reactions ?? [];
    this.mark = entity.mark ?? null;
  }

  /**
   * Put our `emoji` on this channel message (or, with `remove`, take it back — all of ours when
   * `emoji` is empty). Answers the message's reactions after. A channel that cannot show the emoji
   * refuses; the error says which.
   */
  async react(emoji: string, remove = false): Promise<IMessageReaction[]> {
    if (!this.id) throw new Error('react requires this.id');
    const action = new ActionInfo('react', FlowMessage.type, this.id, 'POST');
    action.bodyParameters = { emoji, remove };
    const data = await dataManager.callAction<unknown, { reactions?: IMessageReaction[] }>(action);
    this.reactions = data?.reactions ?? [];
    return this.reactions;
  }

  /** Promote a draft message to a real reply: flips is_draft=false, appends to conversation.jsonl, pushes to hub. */
  async sendDraft(): Promise<{ flow_message_id: string; conversation_id: string; message_count: number }> {
    const action = new ActionInfo('send-draft', 'flow_message', this.id ?? null, 'POST');
    const res = await dataManager.callAction<
      unknown,
      { flow_message_id: string; conversation_id: string; message_count: number }
    >(action);
    return res!;
  }

  /** Delete this message everywhere. Allowed only for the sender of the
   *  message or the conversation owner — the backend (and hub) enforce that
   *  gate. On a shared conversation this fans a DELETE out to every
   *  participant; the message's entire existence (DB row + on-disk record
   *  folder) is erased. POSTs /api/v1/graph/flow_message/<id>/remove-message. */
  async remove(): Promise<void> {
    if (!this.id) throw new Error('remove requires this.id');
    await this.post<unknown>('remove-message');
  }

  // -------- Header / Body interface (principle #6) -------- //

  /** WHEN THIS MESSAGE ENTERED THE CONVERSATION — mirrors
   *  FlowMessage.occurred_at on the backend. Its position in the feed and the
   *  time on its bubble. Deliberately excludes `updated_date`: a message's
   *  place is fixed when it is sent, and a delivery receipt (which bumps the
   *  hub's `updated_date` and syncs back under LWW) must not slide it past
   *  replies already written. Render THIS, never created_date/updated_date. */
  get occurredAt(): string | null {
    const pick = this.sent_at ?? this.created_date ?? null;
    // Base-entity clocks are string|Date on the wire type; normalize to ISO.
    return pick instanceof Date ? pick.toISOString() : pick;
  }

  /** WHEN THIS MESSAGE LAST CHANGED — mirrors FlowMessage.event_time on the
   *  backend: the recency/activity clock behind the stream inbox's "Xm ago". Includes
   *  `updated_date` because a genuine edit IS new activity. For a message's
   *  place in the feed read `occurredAt` instead. */
  get eventTime(): string | null {
    const pick = this.sent_at ?? this.updated_date ?? this.created_date ?? null;
    // Base-entity clocks are string|Date on the wire type; normalize to ISO.
    return pick instanceof Date ? pick.toISOString() : pick;
  }

  /** True iff at least one attachment requires a packed body bundle.
   *  Mirrors flow_sdk.builtin.flow_message.FlowMessage.has_body exactly. */
  hasBody(): boolean {
    for (const att of this.attachment ?? []) {
      if (att.attachment_type === AttachmentType.FILE) return true;
      if (att.attachment_type === AttachmentType.TYPE_ID) return true;
      if (att.attachment_type === AttachmentType.PROMPT && (att.data ?? '').startsWith(PROMPT_FILE_VFS_PREFIX)) {
        return true;
      }
    }
    return false;
  }

  /** Return the underlying attachment list (not a copy). */
  attachments(): Attachment[] {
    return this.attachment ?? [];
  }

  /** Pack + upload this message's body via the local backend, which handles
   *  the hub fs/upload and the body_status state transitions.
   *  POSTs /api/v1/graph/flow_message/<id>/upload_body. */
  async uploadBody(
    _opts: { onProgress?: (pct: number) => void; transferMode?: 'copy' | 'git' | 'both' } = {},
  ): Promise<this> {
    if (!this.id) throw new Error('uploadBody requires this.id');
    await this.post<unknown>('upload_body', { transfer_mode: _opts.transferMode ?? 'copy' });
    this.body_status = BodyStatus.READY;
    this.attachment_filename = BODY_FILENAME;
    return this;
  }

  /** Download + unpack this message's body via the local backend.
   *  Throws BodyNotReadyError when body_status != READY.
   *  POSTs /api/v1/graph/flow_message/<id>/download_body. */
  async downloadBody(_opts: { onProgress?: (pct: number) => void; overwrite?: boolean } = {}): Promise<this> {
    if (this.body_status !== BodyStatus.READY) {
      throw new BodyNotReadyError(this.body_status);
    }
    if (!this.id) throw new Error('downloadBody requires this.id');
    const action = new ActionInfo('download_body', FlowMessage.type, this.id, 'POST');
    // On a genuine collision the backend replies 409 {asset_conflict:true}; the
    // caller can re-invoke with overwrite:true to replace the on-disk asset.
    action.bodyParameters = { overwrite: _opts.overwrite ?? false };
    await dataManager.callAction<unknown, unknown>(action);
    return this;
  }

  /**
   * The single UI entrypoint for pulling this message's attachment bytes onto
   * local disk — every download surface (chips, context panel, prompt build)
   * goes through here rather than building an ``fs/download`` URL by hand.
   *
   * This is frontend gate #1: when there is no downloadable body
   * (``body_status`` NA = none was ever uploaded, UPLOADING = not landed yet)
   * it is a NO-OP — we never fire a request that the backend would have to 404.
   * Only a READY body delegates to ``downloadBody()`` (which posts the
   * ``download_body`` action and unpacks; ``attachment[].local_path`` then
   * materialises and an entity UPDATE re-renders the chips as downloaded).
   */
  async downloadAttachments(opts: { onProgress?: (pct: number) => void } = {}): Promise<this> {
    if (this.body_status !== BodyStatus.READY) return this;
    return this.downloadBody(opts);
  }

  // -------- Realtime receipt (ack) subscription -------- //

  private _ackTapOff: (() => void) | null = null;

  /**
   * Subscribe to receipt changes on THIS message.
   *
   * ``message.on(ConversationEvents.ACK, m => ...)`` fires every time the
   * hub fans a delivery_status update (delivered → received) back for this
   * message id; the callback receives the updated ``IFlowMessage``. Any
   * other event type falls through to the base emitter unchanged.
   */
  override on(eventType: string | string[], callback: Callable): () => void {
    const types = Array.isArray(eventType) ? eventType : [eventType];
    if (types.includes(ConversationEvents.ACK)) {
      this._ensureAckTap();
    }
    const baseOff = super.on(eventType, callback);
    return () => {
      baseOff();
      if (types.includes(ConversationEvents.ACK)) {
        this._maybeTearDownAckTap();
      }
    };
  }

  /**
   * Resolve once this message reaches delivery_status='received' (the
   * receiver has read it), or reject after ``timeoutMs``. Resolves
   * immediately when the message is already received — replaces the manual
   * "poll getById().delivery_status" loop.
   */
  waitForAck(timeoutMs = 15_000): Promise<this> {
    if (this.delivery_status === 'received') return Promise.resolve(this);
    return new Promise<this>((resolve, reject) => {
      let off: (() => void) | null = null;
      const timer = setTimeout(() => {
        off?.();
        reject(new Error(`waitForAck timed out after ${timeoutMs}ms (message ${this.id})`));
      }, timeoutMs);
      off = this.on(ConversationEvents.ACK, (m: IFlowMessage) => {
        if (m.delivery_status !== 'received') return;
        // Mirror the receipt onto this entity so callers can read it
        // straight away regardless of data_op listener ordering.
        this.delivery_status = m.delivery_status;
        this.received_at = m.received_at ?? this.received_at;
        this.delivered_at = m.delivered_at ?? this.delivered_at;
        clearTimeout(timer);
        off?.();
        resolve(this);
      });
    });
  }

  /** Install a low-level data_op tap that emits ``ACK`` for update frames
   *  carrying a delivery_status change on this message id. Idempotent. */
  private _ensureAckTap(): void {
    if (this._ackTapOff) return;
    const cm = ConnectionManager.getInstance();
    const handler = (typeIdStr: string, op: string, data: any) => {
      if (op !== DataOp.UPDATE) return;
      const dash = typeIdStr.indexOf('-');
      if (dash <= 0) return;
      if (typeIdStr.slice(0, dash) !== FlowMessage.type) return;
      if (typeIdStr.slice(dash + 1) !== this.id) return;
      if (!data || data.delivery_status == null) return;
      this.emit(ConversationEvents.ACK, data as IFlowMessage);
    };
    cm.on('on_data_op', handler);
    this._ackTapOff = () => cm.off('on_data_op', handler);
  }

  /** Tear the tap down once the last ACK listener unsubscribes. */
  private _maybeTearDownAckTap(): void {
    if (this._eventListeners.get(ConversationEvents.ACK)?.length) return;
    this._ackTapOff?.();
    this._ackTapOff = null;
  }
}

export interface UploadedAttachment {
  /** The MessageAttachment row staged for review. */
  id: string;
  asset_type: string;
  asset_id: string;
  name: string | null;
}

export interface UploadFlowMessageResult {
  message_id: string;
  task_id: string | null;
  conversation_id: string | null;
  was_new_task: boolean;
  /** What the file staged — reviewable and installable before anything goes live. */
  attachments: UploadedAttachment[];
}

export interface UploadConflict {
  type: string;
  id: string;
}

export async function uploadFlowMessage(
  file: File,
  options: { overwrite?: boolean } = {},
): Promise<UploadFlowMessageResult> {
  const formData = new FormData();
  formData.append('file', file);

  const action = new ActionInfo('flow-message-upload', null, null, 'POST');
  if (options.overwrite) action.queryParameters = { overwrite: 'true' };
  action.bodyParameters = formData;
  const res = await dataManager.callAction<FormData, UploadFlowMessageResult>(action);
  return res!;
}

export interface ExportFlowMessageParams {
  /** The message the recipient reads. */
  text: string;
  /** TypeIds of every entity the file carries (`skill-<uuid>`, …). */
  asset_references: string[];
  /** Pack files git excludes too — set only after the sender saw them (`checkShareAttachments`). */
  include_gitignored?: boolean;
}

/** Pack entities into one `.flowmsg` with no conversation — an offline package
 *  handed to someone as a file. Returns the zip bytes. */
export async function exportFlowMessage(params: ExportFlowMessageParams): Promise<Blob> {
  const action = new ActionInfo('flow-message-export', null, null, 'POST', true, false, null, 'blob');
  action.bodyParameters = { ...params };
  return (await dataManager.callAction<ExportFlowMessageParams, Blob>(action))!;
}

export interface InstallAttachmentsResult {
  installed: string[];
  skipped: string[];
  failed: string[];
  /** Why each failed row failed, keyed by MessageAttachment id. */
  errors: Record<string, string>;
}

/** Install every attachment of one message into a project — the review
 *  dialog's "Install all" for a message that has no conversation. */
export async function installMessageAttachments(
  flowMessageId: string,
  projectId: string,
): Promise<InstallAttachmentsResult> {
  const action = new ActionInfo('install-attachments', FlowMessage.type, flowMessageId, 'POST');
  action.bodyParameters = { project_id: projectId };
  return (await dataManager.callAction<{ project_id: string }, InstallAttachmentsResult>(action))!;
}

/** URL for downloading a local FlowMessage as a `.flowmsg` bundle. */
export function localFlowMessageBundleUrl(flowMessageId: string): string {
  return new ActionInfo('create-and-download-local-flowmsg', FlowMessage.type, flowMessageId, 'GET').fullActionUrl;
}

/** URL for streaming one file from a FlowMessage's embedded VFS storage. */
export function flowMessageAttachmentDownloadUrl(flowMessageId: string, vfsPath: string): string {
  const action = new ActionInfo('fs', FlowMessage.type, flowMessageId, 'GET');
  action.subpath = `download/${vfsPath}`;
  return action.fullActionUrl;
}

export interface MarkResult {
  updated?: string[];
  skipped?: Array<{ id: string; reason: string; current?: string }>;
}

export interface ForwardMessageResult {
  conversation_id: string;
  message_count?: number;
  flow_message_id: string;
  cloned_from_id?: string | null;
}

/**
 * Forward an existing message into another conversation. The backend clones
 * it (new id, the caller as sender, fresh timestamps, `cloned_from_id`
 * provenance, copied attachment bytes) and dispatches the clone through the
 * same pipeline as a fresh send — hub header + body upload included.
 * POSTs /api/v1/graph/flow_message/<id>/forward.
 */
export async function forwardMessage(flowMessageId: string, conversationId: string): Promise<ForwardMessageResult> {
  const action = new ActionInfo('forward', FlowMessage.type, flowMessageId, 'POST');
  action.bodyParameters = { conversation_id: conversationId };
  const res = await dataManager.callAction<{ conversation_id: string }, ForwardMessageResult>(action);
  return res!;
}

/**
 * Batch read-ack: tells the local server (which forwards to the hub) that
 * the listed FlowMessages have been seen by the current user. Hub flips
 * their `delivery_status` to "received" and fans an UPDATE frame back to
 * the sender when the reporting user shares message status.
 */
export async function markFlowMessagesReceived(flow_message_ids: string[]): Promise<MarkResult | null> {
  if (flow_message_ids.length === 0) return null;
  const action = new ActionInfo('mark_received', 'flow_message', null, 'POST');
  action.bodyParameters = { flow_message_ids };
  return dataManager.callAction<{ flow_message_ids: string[] }, MarkResult>(action);
}
